import React, { useEffect, useState, useCallback, useRef } from "react";
import { ref, set, onValue, get, off } from "firebase/database";
import { database } from "../firebase";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import ECGRealTime from "./RealTimeGraph/ECGRealTime";
import HeartRateRealTime from "./RealTimeGraph/HeartRateRealTime";
import { Close as CloseIcon } from "@mui/icons-material";
import SensorsIcon from "@mui/icons-material/Sensors";
import SPO2GraphRealTime from "./RealTimeGraph/Spo2RealTime";
import SkinTemperatureRealTime from "./RealTimeGraph/SkinTemperatureRealTime";
import RealTimePPG from "./RealTimeGraph/RealTimePPG";
import RealTimeAccelereometer from "./RealTimeGraph/RealTimeAccelereometer";
import ElectrodermalActivityRealTime from "./RealTimeGraph/ElectrodermalActivityRealTime";
import {
  fetchHeartbeatForWatch,
  subscribeToHeartbeatStatus,
} from "../utils/watchHeartbeat";

/* Dialog heading per live panel. A map rather than a ternary chain — the old
   chain silently had no entry for PPG, which rendered an empty title. */
const REALTIME_TITLES = {
  ECGRealTime: "Live electrocardiogram",
  HeartRateRealTime: "Live heart rate",
  SkinTemperatureRealTime: "Live skin temperature",
  SPO2GraphRealTime: "Live blood oxygen",
  PPGRealTime: "Live photoplethysmography",
  RealTimeAccelereometer: "Live accelerometer",
  ElectrodermalActivityRealTime: "Live electrodermal activity",
};

const FetchDataButton = ({
  sensorCode,
  label = "Request data",
  variant = "outlined",
  sx,
}) => {
  const [loading, setLoading] = useState(false);
  const [realTimeModalOpen, setRealTimeModalOpen] = useState(false);
  const [currentComponent, setCurrentComponent] = useState(null);
  const [onlineStatus, setOnlineStatus] = useState(null);
  const [supportedSensors, setSupportedSensors] = useState(null);
  const location = useLocation();
  const { watchId } = useParams();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshOnlineStatus = useCallback(async () => {
    if (!watchId) return null;
    try {
      const status = await fetchHeartbeatForWatch(watchId);
      if (isMountedRef.current) {
        setOnlineStatus(status);
      }
      return status;
    } catch (error) {
      console.error("Failed to refresh watch heartbeat", error);
      if (isMountedRef.current) {
        setOnlineStatus(null);
      }
      return null;
    }
  }, [watchId]);

  const parseSupportedSensors = useCallback((rawValue) => {
    if (!rawValue && rawValue !== 0) return [];

    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value));
    }

    if (typeof rawValue === "string") {
      return rawValue
        .split(",")
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isInteger(value));
    }

    if (typeof rawValue === "object" && rawValue !== null) {
      return Object.keys(rawValue)
        .map((key) => Number(key))
        .filter((value) => Number.isInteger(value));
    }

    const numericValue = Number(rawValue);
    return Number.isInteger(numericValue) ? [numericValue] : [];
  }, []);

  const refreshSupportedSensors = useCallback(async () => {
    if (!watchId) return [];

    try {
      const supportedSensorsRef = ref(
        database,
        `Watches/${watchId}/supportedHealthSensors`
      );
      const snapshot = await get(supportedSensorsRef);
      if (!snapshot.exists()) {
        if (isMountedRef.current) {
          setSupportedSensors([]);
        }
        return [];
      }

      const parsed = parseSupportedSensors(snapshot.val());
      if (isMountedRef.current) {
        setSupportedSensors(parsed);
      }
      return parsed;
    } catch (error) {
      console.error("Failed to load supported sensors for watch", error);
      if (isMountedRef.current) {
        setSupportedSensors([]);
      }
      return [];
    }
  }, [parseSupportedSensors, watchId]);

  const renderComponent = () => {
    switch (currentComponent) {
      case "ECGRealTime":
        return <ECGRealTime watchId={watchId} />;
      case "HeartRateRealTime":
        return <HeartRateRealTime watchId={watchId} />;
      case "SkinTemperatureRealTime":
        return <SkinTemperatureRealTime watchId={watchId} />;
      case "SPO2GraphRealTime":
        return <SPO2GraphRealTime watchId={watchId} />;
      case "PPGRealTime":
        return <RealTimePPG watchId={watchId} />;
      case "RealTimeAccelereometer":
        return <RealTimeAccelereometer watchId={watchId} />;
      case "ElectrodermalActivityRealTime":
        return <ElectrodermalActivityRealTime watchId={watchId} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!watchId) return undefined;

    refreshOnlineStatus();
    refreshSupportedSensors();

    const unsubscribe = subscribeToHeartbeatStatus(watchId, (status) => {
      if (isMountedRef.current) {
        setOnlineStatus(status);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [refreshOnlineStatus, refreshSupportedSensors, watchId]);

  useEffect(() => {
    const nodeRef = ref(database, `Watches/${watchId}/onDemandSensor`);

    const handler = onValue(nodeRef, (snap) => {
      if (snap.exists()) {
        const sensorValue = snap.val();

        // Reset loading state if the onDemandSensor value is -1
        if (sensorValue === -1) {
          setLoading(false);
          return;
        }

        // Early exit if path doesn't match
        if (!location.pathname.includes(`/dashboard/${watchId}`)) return;

        switch (sensorValue) {
          case 1:
            setCurrentComponent("ECGRealTime");
            break;
          case 3:
            setCurrentComponent("HeartRateRealTime");
            break;
          case 2:
            setCurrentComponent("SkinTemperatureRealTime");
            break;
          case 4:
            setCurrentComponent("SPO2GraphRealTime");
            break;
          case 5:
            setCurrentComponent("PPGRealTime");
            break;
          case 0:
            setCurrentComponent("RealTimeAccelereometer");
            break;
          case 7:
            setCurrentComponent("ElectrodermalActivityRealTime");
            break;
          case 6:
            return;
          default:
            return;
        }

        if (onlineStatus?.isOnline === false) {
          alert("The watch is offline. Cannot request data.");
        }
      }
    });

    return () => {
      off(nodeRef, "value", handler); // Cleanup listener for onDemandSensor
    };
  }, [location.pathname, onlineStatus?.isOnline, watchId]);

  const handleFetch = async () => {
    const nodeRef = ref(database, `Watches/${watchId}/onDemandSensor`);
    const snap = await get(nodeRef);

    let supported = supportedSensors;
    if (!Array.isArray(supported)) {
      supported = await refreshSupportedSensors();
    }

    const numericSensorCode = Number(sensorCode);
    if (!supported.includes(numericSensorCode)) {
      alert("This watch does not support the selected health sensor.");
      return;
    }

    if (onlineStatus?.isOnline === false) {
      await set(nodeRef, -1);
      alert("The watch is offline. Data cannot be requested.");
      return;
    }

    if (onlineStatus == null) {
      alert("Unable to verify the watch status right now. Please try again.");
      return;
    }

    if (snap.exists() && snap.val() !== -1) {
      alert(
        "Still fetching data. Please wait for the current operation to finish."
      );
      return;
    }

    setLoading(true);

    // Introduce a delay of 3 seconds after checking the online status
    setTimeout(async () => {
      // After the 3-second delay, send the sensorCode to Firebase
      await set(nodeRef, sensorCode); // Setting the sensorCode to fetch data

      if (location.pathname.includes(`/dashboard/${watchId}`)) {
        setRealTimeModalOpen(true); // Open modal after starting the fetch
      }

      if (numericSensorCode === 6) {
        setRealTimeModalOpen(false);
        setCurrentComponent(null);
      }
    }, 3000); // 3000ms delay (3 seconds)
  };


  const offline = onlineStatus?.isOnline === false;

  return (
    <>
      <Button
        variant={variant}
        sx={sx}
        size="small"
        onClick={handleFetch}
        disabled={loading || offline}
        title={
          offline ? "The watch is offline. Data cannot be requested." : undefined
        }
        startIcon={
          loading ? (
            <CircularProgress color="inherit" size={14} />
          ) : (
            <SensorsIcon fontSize="small" />
          )
        }
      >
        {loading ? "Requesting…" : label}
      </Button>

      <Dialog
        open={realTimeModalOpen}
        onClose={() => setRealTimeModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <span>{REALTIME_TITLES[currentComponent] || "Live capture"}</span>
          <IconButton onClick={() => setRealTimeModalOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: "hidden" }}>
          {renderComponent()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FetchDataButton;
